import React, { useEffect, useMemo, useState } from "react";
import api from "../../../apiClient";
import revivalApi from "../../../api/revivalApi";
import {
  listOperationalCustomerAppointments,
  listOperationalCustomerPayments,
} from "../../../api/operationsCustomersApi";
import { clean, money, plainWords } from "../customerFormat";
import { frIdOf, TabNote, TabState } from "./tabShared";

// Everything that happened with this customer, in order, one line each: visits,
// calls, texts, emails, quotes, payments. Built only from sources scoped to the
// customer (party, FieldRoutes mirror link, Abon bookings, quotes). Nothing
// tenant-wide, nothing synthesized.
const toRows = (value) =>
  Array.isArray(value) ? value
    : Array.isArray(value?.results) ? value.results
      : Array.isArray(value?.messages) ? value.messages
        : Array.isArray(value?.calls) ? value.calls
          : Array.isArray(value?.quotes) ? value.quotes
            : [];

const iso = (v) => {
  const d = new Date(v || 0);
  return v && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
};
const stripHtml = (html = "") => String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const clock = (v) => new Date(v).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const dayKey = (v) => new Date(v).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

function partyParams(record) {
  const params = {};
  const partyId = record?.universal_id || record?.raw?.universal_id;
  if (partyId) params.party_universal_id = partyId;
  if (record?.object === "lead" && record?.id) params.lead_id = record.id;
  if (record?.object !== "lead" && record?.raw?.id) params.customer_id = record.raw.id;
  return params;
}

const KIND = {
  visit: "Visit", call: "Call", text: "Text", email: "Email", quote: "Quote", payment: "Payment",
};

export default function TimelineTab({ record }) {
  const [state, setState] = useState("loading");
  const [events, setEvents] = useState([]);
  const [kind, setKind] = useState("");
  const frId = frIdOf(record);
  const customerPk = record?.object !== "lead" ? record?.raw?.id : null;

  useEffect(() => {
    let alive = true;
    const params = partyParams(record);
    const hasParty = Object.keys(params).length > 0;
    (async () => {
      setState("loading");
      const safe = (p) => p.catch(() => null);
      const [msgs, calls, frAppts, frPays, crmAppts, quotes] = await Promise.all([
        hasParty ? safe(api.get("/comms/thread/", { params }).then((r) => toRows(r.data))) : [],
        hasParty ? safe(api.get("/comms/calls/", { params }).then((r) => toRows(r.data))) : [],
        frId ? safe(listOperationalCustomerAppointments(frId, { page_size: 50 }).then((r) => toRows(r.data))) : [],
        frId ? safe(listOperationalCustomerPayments(frId, { page_size: 50 }).then((r) => toRows(r.data))) : [],
        customerPk ? safe(api.get("/appointments/appointments/", { params: { customer_id: customerPk } }).then((r) => toRows(r.data))) : [],
        customerPk ? safe(revivalApi.listQuotes({ customer_id: String(customerPk) }).then((r) => toRows(r.data))) : [],
      ]);
      if (!alive) return;
      const failed = [msgs, calls, frAppts, frPays, crmAppts, quotes].some((x) => x === null);
      const rows = [];
      const callSids = new Set((calls || []).map((c) => c.call_sid).filter(Boolean));

      (msgs || []).forEach((m) => {
        const at = iso(m.created_at || m.timestamp || m.updated_at);
        if (!at) return;
        const ch = clean(m.channel || "sms").toLowerCase();
        // A call's log row is drawn from the calls list below; the voicemail row says what it was.
        if ((ch === "call" || ch === "voicemail") && m.provider_message_id && callSids.has(m.provider_message_id)) return;
        const inbound = clean(m.direction).toLowerCase() === "inbound";
        const isEmail = ch === "email";
        rows.push({
          id: `msg-${m.id || at}`, at, kind: isEmail ? "email" : ch === "voicemail" ? "call" : "text",
          title: isEmail ? `Email ${inbound ? "from them" : "sent"}${m.subject ? ` · ${m.subject}` : ""}` : ch === "voicemail" ? "Voicemail" : `Text ${inbound ? "from them" : "sent"}`,
          detail: clean(stripHtml(m.body_html ? "" : m.body) || m.preview || ""),
          status: plainWords(m.delivery_status || m.status),
        });
      });

      (calls || []).forEach((c) => {
        const at = iso(c.created_at || c.started_at);
        if (!at) return;
        const inbound = clean(c.direction).toLowerCase() === "inbound";
        const secs = Number(c.duration || c.duration_seconds || 0);
        const vm = c.voicemail || c.status === "voicemail";
        rows.push({
          id: `call-${c.call_sid || c.id || at}`, at, kind: "call",
          title: vm ? "Voicemail" : secs ? (inbound ? "Call from them" : "Call placed") : inbound ? "Missed call" : "Call placed, no answer",
          detail: [secs ? `${Math.max(1, Math.round(secs / 60))} min` : "", clean(c.summary || c.ai_summary)].filter(Boolean).join(" · "),
          status: vm ? "" : plainWords(c.outcome || c.status),
        });
      });

      (frAppts || []).forEach((a) => {
        const at = iso(a.completed_at || a.scheduled_start);
        if (!at) return;
        const done = Boolean(a.completed_at);
        rows.push({
          id: `fra-${a.external_id}`, at, kind: "visit",
          title: `${clean(a.service_name) || "Service visit"} ${a.cancelled_at ? "cancelled" : done ? "completed" : "scheduled"}`,
          detail: [clean(a.serviced_by_name), clean(a.notes)].filter(Boolean).join(" — "),
          status: "",
        });
      });

      (crmAppts || [])
        .filter((a) => !(frId && a?.metadata?.fieldroutes_appointment_id))
        .forEach((a) => {
          const at = iso(a.start_time || a.created_at);
          if (!at) return;
          rows.push({ id: `appt-${a.id}`, at, kind: "visit", title: `${clean(a.service_type_name) || "Appointment"} (booked in Abon)`, detail: clean(a.notes), status: plainWords(a.status) });
        });

      (quotes || []).forEach((q) => {
        const at = iso(q.sent_at || q.created_at || q.updated_at);
        if (!at) return;
        const amount = Number(q.total || q.amount || q.grand_total || 0);
        rows.push({
          id: `quote-${q.id}`, at, kind: "quote",
          title: `Quote ${clean(q.quote_number || q.number || q.id)}`.trim(),
          detail: [amount ? money(amount) : "", clean(q.title || q.service_name || q.service)].filter(Boolean).join(" · "),
          status: plainWords(q.status),
        });
      });

      (frPays || []).forEach((p) => {
        const at = iso(p.date);
        if (!at) return;
        const declined = String(p.status) === "0";
        rows.push({ id: `pay-${p.external_id}`, at, kind: "payment", title: declined ? "Payment declined / voided" : "Payment received", detail: money(p.amount, { cents: true }), status: "" });
      });

      rows.sort((a, b) => new Date(b.at) - new Date(a.at));
      setEvents(rows);
      setState(failed ? "partial" : "ok");
    })();
    return () => { alive = false; };
  }, [record, frId, customerPk]);

  const kinds = useMemo(() => [...new Set(events.map((e) => e.kind))], [events]);
  const grouped = useMemo(() => {
    const out = [];
    let key = null;
    events.filter((e) => !kind || e.kind === kind).forEach((e) => {
      const k = dayKey(e.at);
      if (k !== key) { out.push({ key: k, rows: [] }); key = k; }
      out[out.length - 1].rows.push(e);
    });
    return out;
  }, [events, kind]);

  if (state === "loading") return <TabState>Loading activity…</TabState>;
  if (!events.length) {
    return <TabState>No messages, calls, visits, quotes or payments for this customer yet{state === "partial" ? " (some sources couldn't be loaded)" : ""}.</TabState>;
  }

  return (
    <div className="ct-stack tl">
      <div className="tl-bar" role="tablist" aria-label="Filter the timeline">
        <button type="button" className={`ubx-chip ${!kind ? "on" : ""}`} onClick={() => setKind("")}>All</button>
        {kinds.map((k) => (
          <button key={k} type="button" className={`ubx-chip ${kind === k ? "on" : ""}`} onClick={() => setKind(kind === k ? "" : k)}>{KIND[k] || k}</button>
        ))}
      </div>
      {state === "partial" ? <TabNote>Some sources couldn't be loaded; this list may be incomplete.</TabNote> : null}
      {frId ? <TabNote>Latest 50 visits and 50 payments; the Appointments and Invoices tabs have the full history.</TabNote> : null}
      {grouped.map((g) => (
        <section key={g.key} className="tl-day">
          <h4 className="tl-day__head">{g.key}</h4>
          <ol className="tl-list">
            {g.rows.map((e) => (
              <li key={e.id} className={`tl-row tl-row--${e.kind}`}>
                <span className="tl-row__time">{clock(e.at)}</span>
                <span className="tl-row__kind">{KIND[e.kind] || e.kind}</span>
                <span className="tl-row__text" title={e.detail || undefined}>
                  <span className="tl-row__title">{e.title}</span>
                  {e.detail ? <span className="tl-row__detail"> — {e.detail}</span> : null}
                </span>
                {e.status ? <span className="tl-row__status">{e.status}</span> : null}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
